/** Trades Poster — Trade Categories, Subcategories and Public-Facing Names (admin sample data). */

import {
  assignAlphabeticalTradeCategoryIds,
  getPublicCategories,
  getPublicFacingNames,
  normalizeCategoryRecord,
} from '../../../data/tradeCategoryWorkflow';
import { resolveTradeCategoryIconSrc } from '../../../data/tradeCategoryIconMap';

const tradeSub = (id, name, audit = {}) => ({
  id,
  name,
  createdOn: audit.createdOn || '12 May 2026',
  createdBy: audit.createdBy || 'Saim Sahi',
  lastUpdatedOn: audit.lastUpdatedOn || '3 Jul 2026, 10:15 am',
  lastUpdatedBy: audit.lastUpdatedBy || 'Saim Sahi',
});

const publicCategory = ({
  id,
  name,
  sourceTradeSubcategoryId,
  sourceTradeSubcategoryName,
  iconFileName,
}) => ({
  id,
  name,
  sourceTradeSubcategoryId,
  sourceTradeSubcategoryName,
  iconUrl: '',
  iconFileName: iconFileName || `${name.toLowerCase().replace(/\s+/g, '-')}.svg`,
  subcategories: [],
  createdOn: '12 May 2026',
  createdBy: 'Saim Sahi',
  lastUpdatedOn: '3 Jul 2026, 10:15 am',
  lastUpdatedBy: 'Saim Sahi',
});

export const TRADE_CATEGORIES_DUMMY_DATA = [
  {
    id: 'TC-001',
    name: 'Plumbing',
    iconUrl: '',
    iconFileName: 'plumbing.svg',
    tradeSubcategories: [
      tradeSub('SC-001', 'General Plumbing'),
      tradeSub('SC-002', 'Drainage Plumbing'),
      tradeSub('SC-003', 'Hot Water Systems'),
      tradeSub('SC-004', 'Gas Fitting'),
      tradeSub('SC-005', 'Leak Detection'),
      tradeSub('SC-006', 'Bathroom & Kitchen Plumbing'),
      tradeSub('SC-007', 'Roof Plumbing'),
      tradeSub('SC-008', 'Backflow Prevention & TMV'),
      tradeSub('SC-009', 'Water Filtration'),
      tradeSub('SC-010', 'Commercial Plumbing'),
      tradeSub('SC-011', 'Septic & Wastewater'),
    ],
    publicCategories: [
      publicCategory({
        id: 'PC-001',
        name: 'Drainage Plumbing',
        sourceTradeSubcategoryId: 'SC-002',
        sourceTradeSubcategoryName: 'Drainage Plumbing',
        iconFileName: '',
      }),
      publicCategory({
        id: 'PC-002',
        name: 'Hot Water Systems',
        sourceTradeSubcategoryId: 'SC-003',
        sourceTradeSubcategoryName: 'Hot Water Systems',
        iconFileName: '',
      }),
      publicCategory({
        id: 'PC-003',
        name: 'Gas Fitting',
        sourceTradeSubcategoryId: 'SC-004',
        sourceTradeSubcategoryName: 'Gas Fitting',
        iconFileName: 'gas-fitting.svg',
      }),
      publicCategory({
        id: 'PC-004',
        name: 'Roof Plumbing',
        sourceTradeSubcategoryId: 'SC-007',
        sourceTradeSubcategoryName: 'Roof Plumbing',
        iconFileName: '',
      }),
    ],
    linkedRecords: { businesses: 40, livePosts: 64, draftPosts: 5 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-002',
    name: 'Electrical',
    iconUrl: '',
    iconFileName: 'electrical.svg',
    tradeSubcategories: [
      tradeSub('SC-012', 'General Electrical'),
      tradeSub('SC-013', 'Switchboards'),
      tradeSub('SC-014', 'Lighting'),
      tradeSub('SC-015', 'Power Points & Switches'),
      tradeSub('SC-016', 'Safety Switches'),
      tradeSub('SC-017', 'Electrical Rewiring'),
      tradeSub('SC-018', 'Fault Finding & Repairs'),
      tradeSub('SC-019', 'Smoke Alarms'),
      tradeSub('SC-020', 'Data Cabling'),
      tradeSub('SC-021', 'Home Automation'),
      tradeSub('SC-022', 'EV Chargers'),
      tradeSub('SC-023', 'Electric Underfloor Heating'),
      tradeSub('SC-024', 'New Build & Renovation Electrical'),
      tradeSub('SC-025', 'Commercial Electrical'),
    ],
    publicCategories: [
      publicCategory({
        id: 'PC-005',
        name: 'Pendant Lighting',
        sourceTradeSubcategoryId: 'SC-014',
        sourceTradeSubcategoryName: 'Lighting',
        iconFileName: '',
      }),
      publicCategory({
        id: 'PC-006',
        name: 'Smoke Alarms',
        sourceTradeSubcategoryId: 'SC-019',
        sourceTradeSubcategoryName: 'Smoke Alarms',
        iconFileName: '',
      }),
      publicCategory({
        id: 'PC-007',
        name: 'Home Automation',
        sourceTradeSubcategoryId: 'SC-021',
        sourceTradeSubcategoryName: 'Home Automation',
        iconFileName: 'home-automation.svg',
      }),
      publicCategory({
        id: 'PC-008',
        name: 'EV Chargers',
        sourceTradeSubcategoryId: 'SC-022',
        sourceTradeSubcategoryName: 'EV Chargers',
        iconFileName: '',
      }),
      publicCategory({
        id: 'PC-009',
        name: 'Underfloor Heating',
        sourceTradeSubcategoryId: 'SC-023',
        sourceTradeSubcategoryName: 'Electric Underfloor Heating',
        iconFileName: '',
      }),
    ],
    linkedRecords: { businesses: 57, livePosts: 91, draftPosts: 7 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-003',
    name: 'Carpentry',
    iconUrl: '',
    iconFileName: 'carpentry.svg',
    tradeSubcategories: [
      tradeSub('SC-026', 'General Carpentry'),
      tradeSub('SC-027', 'Timber Framing'),
      tradeSub('SC-028', 'Formwork Carpentry'),
      tradeSub('SC-029', 'Renovation Carpentry'),
      tradeSub('SC-030', 'Door Installation & Repairs'),
      tradeSub('SC-031', 'Window Installation & Repairs'),
      tradeSub('SC-032', 'Decking'),
      tradeSub('SC-033', 'Pergolas & Verandahs'),
      tradeSub('SC-034', 'Carports'),
      tradeSub('SC-035', 'Cladding'),
      tradeSub('SC-036', 'Interior Carpentry & Finishing'),
      tradeSub('SC-037', 'Timber Repairs & Maintenance'),
      tradeSub('SC-038', 'Residential Fit-Outs'),
    ],
    publicCategories: [
      publicCategory({
        id: 'PC-010',
        name: 'Decking',
        sourceTradeSubcategoryId: 'SC-032',
        sourceTradeSubcategoryName: 'Decking',
        iconFileName: 'decking.svg',
      }),
      publicCategory({
        id: 'PC-011',
        name: 'Pergolas & Verandahs',
        sourceTradeSubcategoryId: 'SC-033',
        sourceTradeSubcategoryName: 'Pergolas & Verandahs',
        iconFileName: 'pergolas.svg',
      }),
      publicCategory({
        id: 'PC-012',
        name: 'Cladding',
        sourceTradeSubcategoryId: 'SC-035',
        sourceTradeSubcategoryName: 'Cladding',
        iconFileName: '',
      }),
      publicCategory({
        id: 'PC-013',
        name: 'Wall Panelling',
        sourceTradeSubcategoryId: 'SC-036',
        sourceTradeSubcategoryName: 'Interior Carpentry & Finishing',
        iconFileName: '',
      }),
    ],
    linkedRecords: { businesses: 74, livePosts: 118, draftPosts: 9 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-004',
    name: 'Cabinet Making',
    iconUrl: '',
    iconFileName: 'cabinet-making.svg',
    tradeSubcategories: [
      tradeSub('SC-039', 'Kitchen Cabinets'),
      tradeSub('SC-040', 'Bathroom Vanities'),
      tradeSub('SC-041', 'Laundry Cabinets'),
      tradeSub('SC-042', 'Wardrobes'),
      tradeSub('SC-043', 'Built-In Furniture & Storage'),
      tradeSub('SC-044', 'Bookcases & Shelving'),
      tradeSub('SC-045', 'Entertainment Units'),
      tradeSub('SC-046', 'Home Office & Study Cabinetry'),
      tradeSub('SC-047', 'Garage Storage Cabinets'),
      tradeSub('SC-048', 'Office Cabinetry'),
      tradeSub('SC-049', 'Custom Cabinets'),
      tradeSub('SC-050', 'Laminate & Timber Benchtops'),
      tradeSub('SC-051', 'Cabinet Repairs & Refacing'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 91, livePosts: 146, draftPosts: 11 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-005',
    name: 'Builders',
    iconUrl: '',
    iconFileName: '',
    tradeSubcategories: [
      tradeSub('SC-052', 'General Building'),
      tradeSub('SC-053', 'New Home Builds'),
      tradeSub('SC-054', 'Home Renovations'),
      tradeSub('SC-055', 'Home Extensions'),
      tradeSub('SC-056', 'Bathroom Renovations'),
      tradeSub('SC-057', 'Kitchen Renovations'),
      tradeSub('SC-058', 'Second-Storey Additions'),
      tradeSub('SC-059', 'Granny Flats'),
      tradeSub('SC-060', 'Knockdown & Rebuild'),
      tradeSub('SC-061', 'Structural Alterations'),
      tradeSub('SC-062', 'Multi-Unit & Townhouse Builds'),
      tradeSub('SC-063', 'Commercial Building'),
      tradeSub('SC-064', 'Building Project Management'),
    ],
    publicCategories: [
      publicCategory({
        id: 'PC-014',
        name: 'Bathroom Renovations',
        sourceTradeSubcategoryId: 'SC-056',
        sourceTradeSubcategoryName: 'Bathroom Renovations',
        iconFileName: '',
      }),
      publicCategory({
        id: 'PC-015',
        name: 'Kitchen Renovations',
        sourceTradeSubcategoryId: 'SC-057',
        sourceTradeSubcategoryName: 'Kitchen Renovations',
        iconFileName: '',
      }),
      publicCategory({
        id: 'PC-016',
        name: 'Home Extensions',
        sourceTradeSubcategoryId: 'SC-055',
        sourceTradeSubcategoryName: 'Home Extensions',
        iconFileName: 'renovation.svg',
      }),
    ],
    linkedRecords: { businesses: 108, livePosts: 173, draftPosts: 13 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-006',
    name: 'Restumping & Underpinning',
    iconUrl: '',
    iconFileName: '',
    tradeSubcategories: [
      tradeSub('SC-065', 'House Restumping & Reblocking'),
      tradeSub('SC-066', 'Underpinning'),
      tradeSub('SC-067', 'Slab Underpinning'),
      tradeSub('SC-068', 'House Levelling'),
      tradeSub('SC-069', 'Foundation Repairs & Stabilisation'),
      tradeSub('SC-070', 'Stump & Pier Replacement'),
      tradeSub('SC-071', 'Bearer & Joist Repairs'),
      tradeSub('SC-072', 'Subfloor Structural Repairs'),
      tradeSub('SC-073', 'Commercial Restumping & Underpinning'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 125, livePosts: 200, draftPosts: 15 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-007',
    name: 'Roofing',
    iconUrl: '',
    iconFileName: 'roofing.svg',
    tradeSubcategories: [
      tradeSub('SC-074', 'General Roofing'),
      tradeSub('SC-075', 'Roof Repairs'),
      tradeSub('SC-076', 'Roof Replacement'),
      tradeSub('SC-077', 'Roof Restoration'),
      tradeSub('SC-078', 'Metal Roofing'),
      tradeSub('SC-079', 'Roof Tiling'),
      tradeSub('SC-080', 'Flat Roofing'),
      tradeSub('SC-081', 'Skylights'),
      tradeSub('SC-082', 'Roof Ventilation'),
      tradeSub('SC-083', 'Commercial Roofing'),
    ],
    publicCategories: [
      publicCategory({
        id: 'PC-017',
        name: 'Roof Restoration',
        sourceTradeSubcategoryId: 'SC-077',
        sourceTradeSubcategoryName: 'Roof Restoration',
        iconFileName: '',
      }),
      publicCategory({
        id: 'PC-018',
        name: 'Skylights',
        sourceTradeSubcategoryId: 'SC-081',
        sourceTradeSubcategoryName: 'Skylights',
        iconFileName: '',
      }),
    ],
    linkedRecords: { businesses: 142, livePosts: 227, draftPosts: 17 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-008',
    name: 'Painting & Decorating',
    iconUrl: '',
    iconFileName: 'painting-and-decorating.svg',
    tradeSubcategories: [
      tradeSub('SC-084', 'Interior Painting'),
      tradeSub('SC-085', 'Exterior Painting'),
      tradeSub('SC-086', 'New Build Painting'),
      tradeSub('SC-087', 'Roof Painting'),
      tradeSub('SC-088', 'Commercial Painting'),
      tradeSub('SC-089', 'Industrial Painting'),
      tradeSub('SC-090', 'Wallpapering'),
      tradeSub('SC-091', 'Decorative Finishes'),
      tradeSub('SC-092', 'Spray Painting'),
      tradeSub('SC-093', 'Paint Repairs & Touch-Ups'),
      tradeSub('SC-094', 'Protective Coatings'),
      tradeSub('SC-095', 'Timber Staining & Finishing'),
    ],
    publicCategories: [
      publicCategory({
        id: 'PC-019',
        name: 'Wallpapering',
        sourceTradeSubcategoryId: 'SC-090',
        sourceTradeSubcategoryName: 'Wallpapering',
        iconFileName: '',
      }),
      publicCategory({
        id: 'PC-020',
        name: 'Roof Painting',
        sourceTradeSubcategoryId: 'SC-087',
        sourceTradeSubcategoryName: 'Roof Painting',
        iconFileName: '',
      }),
    ],
    linkedRecords: { businesses: 159, livePosts: 254, draftPosts: 19 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-009',
    name: 'Tiling',
    iconUrl: '',
    iconFileName: 'tiling.svg',
    tradeSubcategories: [
      tradeSub('SC-096', 'General Tiling'),
      tradeSub('SC-097', 'Floor Tiling'),
      tradeSub('SC-098', 'Wall Tiling'),
      tradeSub('SC-099', 'Bathroom Tiling'),
      tradeSub('SC-100', 'Kitchen & Splashback Tiling'),
      tradeSub('SC-101', 'Outdoor Tiling'),
      tradeSub('SC-102', 'Pool Tiling'),
      tradeSub('SC-103', 'Mosaic Tiling'),
      tradeSub('SC-104', 'Large-Format Tiling'),
      tradeSub('SC-105', 'Natural Stone Tiling'),
      tradeSub('SC-106', 'Commercial Tiling'),
      tradeSub('SC-107', 'Tile Repairs & Replacement'),
      tradeSub('SC-108', 'Regrouting'),
      tradeSub('SC-109', 'Tile Resurfacing'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 176, livePosts: 282, draftPosts: 21 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-010',
    name: 'Flooring',
    iconUrl: '',
    iconFileName: 'flooring.svg',
    tradeSubcategories: [
      tradeSub('SC-110', 'General Flooring'),
      tradeSub('SC-111', 'Solid Timber Flooring'),
      tradeSub('SC-112', 'Engineered Timber Flooring'),
      tradeSub('SC-113', 'Laminate Flooring'),
      tradeSub('SC-114', 'Vinyl Flooring'),
      tradeSub('SC-115', 'Hybrid Flooring'),
      tradeSub('SC-116', 'Carpet Flooring'),
      tradeSub('SC-117', 'Bamboo Flooring'),
      tradeSub('SC-118', 'Parquetry Flooring'),
      tradeSub('SC-119', 'Floor Sanding & Polishing'),
      tradeSub('SC-120', 'Epoxy Flooring'),
      tradeSub('SC-121', 'Floor Levelling & Preparation'),
      tradeSub('SC-122', 'Floor Repairs'),
      tradeSub('SC-123', 'Commercial Flooring'),
    ],
    publicCategories: [
      publicCategory({
        id: 'PC-021',
        name: 'Carpet',
        sourceTradeSubcategoryId: 'SC-116',
        sourceTradeSubcategoryName: 'Carpet Flooring',
        iconFileName: 'carpetandflooring.svg',
      }),
      publicCategory({
        id: 'PC-022',
        name: 'Epoxy Flooring',
        sourceTradeSubcategoryId: 'SC-120',
        sourceTradeSubcategoryName: 'Epoxy Flooring',
        iconFileName: '',
      }),
      publicCategory({
        id: 'PC-023',
        name: 'Self-Levelling',
        sourceTradeSubcategoryId: 'SC-121',
        sourceTradeSubcategoryName: 'Floor Levelling & Preparation',
        iconFileName: '',
      }),
    ],
    linkedRecords: { businesses: 193, livePosts: 309, draftPosts: 23 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-011',
    name: 'Plastering',
    iconUrl: '',
    iconFileName: 'plastering.svg',
    tradeSubcategories: [
      tradeSub('SC-124', 'General Plastering'),
      tradeSub('SC-125', 'New Plastering'),
      tradeSub('SC-126', 'Plaster Repairs'),
      tradeSub('SC-127', 'Wall Plastering'),
      tradeSub('SC-128', 'Ceiling Plastering & Repairs'),
      tradeSub('SC-129', 'Plasterboard Installation'),
      tradeSub('SC-130', 'Partition Walls'),
      tradeSub('SC-131', 'Cornices'),
      tradeSub('SC-132', 'Suspended Ceilings'),
      tradeSub('SC-133', 'Decorative & Ornamental Plastering'),
      tradeSub('SC-134', 'Commercial Plastering'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 50, livePosts: 80, draftPosts: 6 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-012',
    name: 'Bricklaying',
    iconUrl: '',
    iconFileName: 'bricklaying.svg',
    tradeSubcategories: [
      tradeSub('SC-135', 'General Bricklaying'),
      tradeSub('SC-136', 'New Brickwork'),
      tradeSub('SC-137', 'Brick Alterations & Additions'),
      tradeSub('SC-138', 'Brick Repairs'),
      tradeSub('SC-139', 'Blockwork'),
      tradeSub('SC-140', 'Brick Repointing'),
      tradeSub('SC-141', 'Brick Piers & Columns'),
      tradeSub('SC-142', 'Brick Fences'),
      tradeSub('SC-143', 'Garden & Boundary Walls'),
      tradeSub('SC-144', 'Chimneys & Fireplaces'),
      tradeSub('SC-145', 'Brick Letterboxes'),
      tradeSub('SC-146', 'Brick Restoration'),
      tradeSub('SC-147', 'Commercial Bricklaying'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 67, livePosts: 107, draftPosts: 8 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-013',
    name: 'Concreting',
    iconUrl: '',
    iconFileName: 'concreting.svg',
    tradeSubcategories: [
      tradeSub('SC-148', 'General Concreting'),
      tradeSub('SC-149', 'Concrete Slabs'),
      tradeSub('SC-150', 'Driveways & Crossovers'),
      tradeSub('SC-151', 'Footpaths & Pathways'),
      tradeSub('SC-152', 'Foundations & Footings'),
      tradeSub('SC-153', 'Exposed Aggregate'),
      tradeSub('SC-154', 'Coloured & Decorative Concrete'),
      tradeSub('SC-155', 'Polished Concrete'),
      tradeSub('SC-156', 'Concrete Grinding'),
      tradeSub('SC-157', 'Concrete Resurfacing'),
      tradeSub('SC-158', 'Concrete Repairs'),
      tradeSub('SC-159', 'Concrete Cutting & Sawing'),
      tradeSub('SC-160', 'Concrete Sealing'),
      tradeSub('SC-161', 'Commercial Concreting'),
    ],
    publicCategories: [
      publicCategory({
        id: 'PC-024',
        name: 'Polished Concrete',
        sourceTradeSubcategoryId: 'SC-155',
        sourceTradeSubcategoryName: 'Polished Concrete',
        iconFileName: '',
      }),
      publicCategory({
        id: 'PC-025',
        name: 'Concrete Grinding',
        sourceTradeSubcategoryId: 'SC-156',
        sourceTradeSubcategoryName: 'Concrete Grinding',
        iconFileName: '',
      }),
    ],
    linkedRecords: { businesses: 84, livePosts: 134, draftPosts: 10 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-014',
    name: 'Waterproofing',
    iconUrl: '',
    iconFileName: 'waterproofing.svg',
    tradeSubcategories: [
      tradeSub('SC-162', 'General Waterproofing'),
      tradeSub('SC-163', 'Bathroom & Wet Area Waterproofing'),
      tradeSub('SC-164', 'Balcony & Deck Waterproofing'),
      tradeSub('SC-165', 'Roof Waterproofing'),
      tradeSub('SC-166', 'Basement & Below-Ground Waterproofing'),
      tradeSub('SC-167', 'External Wall Waterproofing'),
      tradeSub('SC-168', 'Retaining Wall Waterproofing'),
      tradeSub('SC-169', 'Planter Box Waterproofing'),
      tradeSub('SC-170', 'Pool Waterproofing'),
      tradeSub('SC-171', 'Remedial Waterproofing & Leak Repairs'),
      tradeSub('SC-172', 'Damp Proofing & Rising Damp'),
      tradeSub('SC-173', 'Commercial Waterproofing'),
    ],
    publicCategories: [
      publicCategory({
        id: 'PC-026',
        name: 'Leak Repairs',
        sourceTradeSubcategoryId: 'SC-171',
        sourceTradeSubcategoryName: 'Remedial Waterproofing & Leak Repairs',
        iconFileName: '',
      }),
      publicCategory({
        id: 'PC-027',
        name: 'Rising Damp',
        sourceTradeSubcategoryId: 'SC-172',
        sourceTradeSubcategoryName: 'Damp Proofing & Rising Damp',
        iconFileName: 'waterproofing.svg',
      }),
    ],
    linkedRecords: { businesses: 101, livePosts: 162, draftPosts: 12 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-015',
    name: 'Demolition',
    iconUrl: '',
    iconFileName: 'demolition.svg',
    tradeSubcategories: [
      tradeSub('SC-174', 'General Demolition'),
      tradeSub('SC-175', 'Residential Demolition'),
      tradeSub('SC-176', 'Commercial Demolition'),
      tradeSub('SC-177', 'Internal Demolition & Strip-Outs'),
      tradeSub('SC-178', 'Partial Demolition'),
      tradeSub('SC-179', 'Bathroom & Kitchen Demolition'),
      tradeSub('SC-180', 'Shed & Garage Demolition'),
      tradeSub('SC-181', 'Concrete Removal'),
      tradeSub('SC-182', 'Pool Demolition & Removal'),
      tradeSub('SC-183', 'Site Clearing'),
      tradeSub('SC-184', 'Asbestos Removal'),
      tradeSub('SC-185', 'Friable Asbestos Removal'),
      tradeSub('SC-186', 'Non-Friable Asbestos Removal'),
      tradeSub('SC-187', 'Asbestos Roof Removal'),
      tradeSub('SC-188', 'Asbestos Fence Removal'),
      tradeSub('SC-189', 'Asbestos Soil Remediation'),
      tradeSub('SC-190', 'Asbestos Disposal'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 118, livePosts: 189, draftPosts: 14 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-016',
    name: 'Scaffolding',
    iconUrl: '',
    iconFileName: 'scafolding.svg',
    tradeSubcategories: [
      tradeSub('SC-191', 'Residential Scaffolding'),
      tradeSub('SC-192', 'Commercial Scaffolding'),
      tradeSub('SC-193', 'Industrial Scaffolding'),
      tradeSub('SC-194', 'Scaffolding Hire'),
      tradeSub('SC-195', 'Scaffold Erection & Dismantling'),
      tradeSub('SC-196', 'Mobile Scaffold Towers'),
      tradeSub('SC-197', 'Suspended Scaffolding'),
      tradeSub('SC-198', 'Birdcage Scaffolding'),
      tradeSub('SC-199', 'Edge Protection Systems'),
      tradeSub('SC-200', 'Temporary Roof Scaffolding'),
      tradeSub('SC-201', 'Scaffold Alterations & Maintenance'),
      tradeSub('SC-202', 'Scaffold Inspections'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 135, livePosts: 216, draftPosts: 16 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-017',
    name: 'Rubbish Removal',
    iconUrl: '',
    iconFileName: 'skipbins.svg',
    tradeSubcategories: [
      tradeSub('SC-203', 'General Rubbish Removal'),
      tradeSub('SC-204', 'Household Rubbish Removal'),
      tradeSub('SC-205', 'Hard Rubbish Removal'),
      tradeSub('SC-206', 'Green Waste Removal'),
      tradeSub('SC-207', 'Construction & Renovation Waste Removal'),
      tradeSub('SC-208', 'Commercial Rubbish Removal'),
      tradeSub('SC-209', 'Property Clearances'),
      tradeSub('SC-210', 'Deceased Estate Clearances'),
      tradeSub('SC-211', 'Garage & Shed Clearances'),
      tradeSub('SC-212', 'Skip Bin Hire'),
    ],
    publicCategories: [
      publicCategory({
        id: 'PC-028',
        name: 'Skip Bins',
        sourceTradeSubcategoryId: 'SC-212',
        sourceTradeSubcategoryName: 'Skip Bin Hire',
        iconFileName: 'skipbins.svg',
      }),
    ],
    linkedRecords: { businesses: 152, livePosts: 243, draftPosts: 18 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-018',
    name: 'Excavation',
    iconUrl: '',
    iconFileName: 'excavation.svg',
    tradeSubcategories: [
      tradeSub('SC-213', 'General Excavation'),
      tradeSub('SC-214', 'Site Cuts & Site Preparation'),
      tradeSub('SC-215', 'Trenching'),
      tradeSub('SC-216', 'Foundation & Footing Excavation'),
      tradeSub('SC-217', 'Pool Excavation'),
      tradeSub('SC-218', 'Drainage Excavation'),
      tradeSub('SC-219', 'Basement Excavation'),
      tradeSub('SC-220', 'Tight-Access Excavation'),
      tradeSub('SC-221', 'Earthmoving'),
      tradeSub('SC-222', 'Rock Breaking'),
      tradeSub('SC-223', 'Soil & Spoil Removal'),
      tradeSub('SC-224', 'Backfilling & Compaction'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 169, livePosts: 270, draftPosts: 20 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-019',
    name: 'Landscaping',
    iconUrl: '',
    iconFileName: 'landscaping.svg',
    tradeSubcategories: [
      tradeSub('SC-225', 'Landscape Design'),
      tradeSub('SC-226', 'Garden Landscaping'),
      tradeSub('SC-227', 'Gardening & Garden Maintenance'),
      tradeSub('SC-228', 'Lawn Mowing'),
      tradeSub('SC-229', 'Turf Installation'),
      tradeSub('SC-230', 'Artificial Grass'),
      tradeSub('SC-231', 'Paving'),
      tradeSub('SC-232', 'Retaining Walls'),
      tradeSub('SC-233', 'Irrigation Systems'),
      tradeSub('SC-234', 'Garden Beds & Edging'),
      tradeSub('SC-235', 'Landscape Drainage'),
      tradeSub('SC-236', 'Grading & Land Levelling'),
      tradeSub('SC-237', 'Commercial Landscaping'),
    ],
    publicCategories: [
      publicCategory({
        id: 'PC-029',
        name: 'Retaining Walls',
        sourceTradeSubcategoryId: 'SC-232',
        sourceTradeSubcategoryName: 'Retaining Walls',
        iconFileName: '',
      }),
      publicCategory({
        id: 'PC-030',
        name: 'Turf Installation',
        sourceTradeSubcategoryId: 'SC-229',
        sourceTradeSubcategoryName: 'Turf Installation',
        iconFileName: '',
      }),
    ],
    linkedRecords: { businesses: 186, livePosts: 298, draftPosts: 22 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-020',
    name: 'Tree Services',
    iconUrl: '',
    iconFileName: 'tree-services.svg',
    tradeSubcategories: [
      tradeSub('SC-238', 'General Tree Services'),
      tradeSub('SC-239', 'Tree Removal'),
      tradeSub('SC-240', 'Tree Pruning & Trimming'),
      tradeSub('SC-241', 'Stump Grinding & Removal'),
      tradeSub('SC-242', 'Arborist Reports'),
      tradeSub('SC-243', 'Tree Health & Risk Assessments'),
      tradeSub('SC-244', 'Emergency Tree Work'),
      tradeSub('SC-245', 'Palm Tree Services'),
      tradeSub('SC-246', 'Land Clearing'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 43, livePosts: 69, draftPosts: 5 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-021',
    name: 'Fencing & Gates',
    iconUrl: '',
    iconFileName: 'fencing-and-gates.svg',
    tradeSubcategories: [
      tradeSub('SC-247', 'General Fencing'),
      tradeSub('SC-248', 'Timber Fencing'),
      tradeSub('SC-249', 'COLORBOND Fencing'),
      tradeSub('SC-250', 'Aluminium Fencing'),
      tradeSub('SC-251', 'Tubular Steel Fencing'),
      tradeSub('SC-252', 'Pool Fencing'),
      tradeSub('SC-253', 'Security Fencing'),
      tradeSub('SC-254', 'Rural & Farm Fencing'),
      tradeSub('SC-255', 'Boundary Fencing'),
      tradeSub('SC-256', 'Gates'),
      tradeSub('SC-257', 'Automatic & Electric Gates'),
      tradeSub('SC-258', 'Fence & Gate Repairs'),
      tradeSub('SC-259', 'Commercial Fencing'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 60, livePosts: 96, draftPosts: 7 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-022',
    name: 'HVAC',
    iconUrl: '',
    iconFileName: 'air-conditioning.svg',
    tradeSubcategories: [
      tradeSub('SC-260', 'General HVAC'),
      tradeSub('SC-261', 'Air Conditioning'),
      tradeSub('SC-262', 'Split Systems'),
      tradeSub('SC-263', 'Ducted Air Conditioning'),
      tradeSub('SC-264', 'Heating Systems'),
      tradeSub('SC-265', 'Ducted Heating'),
      tradeSub('SC-266', 'Evaporative Cooling'),
      tradeSub('SC-267', 'Commercial Refrigeration'),
      tradeSub('SC-268', 'Ventilation Systems'),
      tradeSub('SC-269', 'HVAC Repairs & Maintenance'),
      tradeSub('SC-270', 'Commercial HVAC'),
    ],
    publicCategories: [
      publicCategory({
        id: 'PC-031',
        name: 'Air Conditioning',
        sourceTradeSubcategoryId: 'SC-261',
        sourceTradeSubcategoryName: 'Air Conditioning',
        iconFileName: 'air-conditioning.svg',
      }),
      publicCategory({
        id: 'PC-032',
        name: 'Commercial Refrigeration',
        sourceTradeSubcategoryId: 'SC-267',
        sourceTradeSubcategoryName: 'Commercial Refrigeration',
        iconFileName: 'refrigeration.svg',
      }),
    ],
    linkedRecords: { businesses: 77, livePosts: 123, draftPosts: 9 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-023',
    name: 'Solar Systems',
    iconUrl: '',
    iconFileName: 'solar-power.svg',
    tradeSubcategories: [
      tradeSub('SC-271', 'General Solar Systems'),
      tradeSub('SC-272', 'Solar Panel Installation'),
      tradeSub('SC-273', 'Solar Battery Systems'),
      tradeSub('SC-274', 'Solar Inverters'),
      tradeSub('SC-275', 'Off-Grid Solar Systems'),
      tradeSub('SC-276', 'Solar Repairs'),
      tradeSub('SC-277', 'Solar System Maintenance'),
      tradeSub('SC-278', 'Solar System Upgrades'),
      tradeSub('SC-279', 'Commercial Solar Systems'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 94, livePosts: 150, draftPosts: 11 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-024',
    name: 'Glass & Glazing',
    iconUrl: '',
    iconFileName: 'glass-and-glazing.svg',
    tradeSubcategories: [
      tradeSub('SC-280', 'General Glazing'),
      tradeSub('SC-281', 'Window Glass Replacement'),
      tradeSub('SC-282', 'Door Glass Replacement'),
      tradeSub('SC-283', 'Emergency Glass Repairs'),
      tradeSub('SC-284', 'Shower Screens'),
      tradeSub('SC-285', 'Glass Pool Fencing'),
      tradeSub('SC-286', 'Glass Balustrades'),
      tradeSub('SC-287', 'Mirrors'),
      tradeSub('SC-288', 'Glass Splashbacks'),
      tradeSub('SC-289', 'Glass Partitions'),
      tradeSub('SC-290', 'Double Glazing'),
      tradeSub('SC-291', 'Commercial Glazing'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 111, livePosts: 178, draftPosts: 13 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-025',
    name: 'Garage Doors',
    iconUrl: '',
    iconFileName: '',
    tradeSubcategories: [
      tradeSub('SC-292', 'Garage Door Installation'),
      tradeSub('SC-293', 'Garage Door Replacement'),
      tradeSub('SC-294', 'Garage Door Repairs'),
      tradeSub('SC-295', 'Garage Door Servicing'),
      tradeSub('SC-296', 'Roller Doors'),
      tradeSub('SC-297', 'Sectional Garage Doors'),
      tradeSub('SC-298', 'Tilt Garage Doors'),
      tradeSub('SC-299', 'Garage Door Motors & Automation'),
      tradeSub('SC-300', 'Remotes & Controls'),
      tradeSub('SC-301', 'Commercial Garage Doors'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 128, livePosts: 205, draftPosts: 15 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-026',
    name: 'Pools & Spas',
    iconUrl: '',
    iconFileName: 'pools-and-spas.svg',
    tradeSubcategories: [
      tradeSub('SC-302', 'Pool Construction'),
      tradeSub('SC-303', 'Pool Renovations'),
      tradeSub('SC-304', 'Pool Repairs'),
      tradeSub('SC-305', 'Pool Resurfacing'),
      tradeSub('SC-306', 'Pool Cleaning & Maintenance'),
      tradeSub('SC-307', 'Pool Equipment & Pumps'),
      tradeSub('SC-308', 'Pool Heating'),
      tradeSub('SC-309', 'Pool Leak Detection'),
      tradeSub('SC-310', 'Spa Installation'),
      tradeSub('SC-311', 'Spa Repairs & Maintenance'),
      tradeSub('SC-312', 'Commercial Pools & Spas'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 145, livePosts: 232, draftPosts: 17 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-027',
    name: 'Pest Control',
    iconUrl: '',
    iconFileName: 'pest-control.svg',
    tradeSubcategories: [
      tradeSub('SC-313', 'General Pest Control'),
      tradeSub('SC-314', 'Termite Inspections'),
      tradeSub('SC-315', 'Termite Treatments'),
      tradeSub('SC-316', 'Pre-Purchase Pest Inspections'),
      tradeSub('SC-317', 'Rodent Control'),
      tradeSub('SC-318', 'Cockroach Control'),
      tradeSub('SC-319', 'Ant Control'),
      tradeSub('SC-320', 'Spider Control'),
      tradeSub('SC-321', 'Bed Bug Treatment'),
      tradeSub('SC-322', 'Flea Treatment'),
      tradeSub('SC-323', 'Wasp & Bee Removal'),
      tradeSub('SC-324', 'Possum Removal'),
      tradeSub('SC-325', 'Bird Control'),
      tradeSub('SC-326', 'Commercial Pest Control'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 162, livePosts: 259, draftPosts: 19 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-028',
    name: 'Cleaning Services',
    iconUrl: '',
    iconFileName: 'cleaning-services.svg',
    tradeSubcategories: [
      tradeSub('SC-327', 'General Cleaning'),
      tradeSub('SC-328', 'House Cleaning'),
      tradeSub('SC-329', 'End-of-Lease Cleaning'),
      tradeSub('SC-330', 'Commercial & Office Cleaning'),
      tradeSub('SC-331', 'Carpet Cleaning'),
      tradeSub('SC-332', 'Upholstery Cleaning'),
      tradeSub('SC-333', 'Window Cleaning'),
      tradeSub('SC-334', 'Pressure Cleaning'),
      tradeSub('SC-335', 'Builders Cleaning'),
      tradeSub('SC-336', 'Tile & Grout Cleaning'),
      tradeSub('SC-337', 'Mould Cleaning & Remediation'),
      tradeSub('SC-338', 'Deep Cleaning'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 179, livePosts: 286, draftPosts: 21 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-029',
    name: 'Blinds & Curtains',
    iconUrl: '',
    iconFileName: 'blinds-and-curtains.svg',
    tradeSubcategories: [
      tradeSub('SC-339', 'Blind Installation'),
      tradeSub('SC-340', 'Curtain Installation'),
      tradeSub('SC-341', 'Shutters'),
      tradeSub('SC-342', 'Awnings & External Shades'),
      tradeSub('SC-343', 'Outdoor Blinds'),
      tradeSub('SC-344', 'Motorised Blinds & Curtains'),
      tradeSub('SC-345', 'Blind Repairs'),
      tradeSub('SC-346', 'Curtain Repairs'),
      tradeSub('SC-347', 'Commercial Window Furnishings'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 196, livePosts: 314, draftPosts: 24 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-030',
    name: 'Building Consultants',
    iconUrl: '',
    iconFileName: 'drafting.svg',
    tradeSubcategories: [
      tradeSub('SC-348', 'Building & Construction Consultancy'),
      tradeSub('SC-349', 'Building Design & Drafting'),
      tradeSub('SC-350', 'Interior Design'),
      tradeSub('SC-351', 'Land Surveying'),
      tradeSub('SC-352', 'Structural Engineering'),
      tradeSub('SC-353', 'Civil Engineering'),
      tradeSub('SC-354', 'Building & Planning Permits'),
      tradeSub('SC-355', 'Quantity Surveying & Cost Planning'),
      tradeSub('SC-356', 'Building Code & Compliance Advice'),
      tradeSub('SC-357', 'Sustainability & Energy Assessments'),
    ],
    publicCategories: [
      publicCategory({
        id: 'PC-033',
        name: 'Building Drafting',
        sourceTradeSubcategoryId: 'SC-349',
        sourceTradeSubcategoryName: 'Building Design & Drafting',
        iconFileName: '',
      }),
      publicCategory({
        id: 'PC-034',
        name: 'Interior Design',
        sourceTradeSubcategoryId: 'SC-350',
        sourceTradeSubcategoryName: 'Interior Design',
        iconFileName: 'interior-design.svg',
      }),
      publicCategory({
        id: 'PC-035',
        name: 'Land Surveying',
        sourceTradeSubcategoryId: 'SC-351',
        sourceTradeSubcategoryName: 'Land Surveying',
        iconFileName: 'surveying.svg',
      }),
      publicCategory({
        id: 'PC-036',
        name: 'Structural Engineering',
        sourceTradeSubcategoryId: 'SC-352',
        sourceTradeSubcategoryName: 'Structural Engineering',
        iconFileName: 'engineering-services.svg',
      }),
      publicCategory({
        id: 'PC-037',
        name: 'Building Permits',
        sourceTradeSubcategoryId: 'SC-354',
        sourceTradeSubcategoryName: 'Building & Planning Permits',
        iconFileName: 'plans-and-permits.svg',
      }),
    ],
    linkedRecords: { businesses: 53, livePosts: 85, draftPosts: 6 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-031',
    name: 'Building Inspections',
    iconUrl: '',
    iconFileName: 'building-inspection.svg',
    tradeSubcategories: [
      tradeSub('SC-358', 'General Building Inspections'),
      tradeSub('SC-359', 'Pre-Purchase Building Inspections'),
      tradeSub('SC-360', 'Pre-Sale Building Inspections'),
      tradeSub('SC-361', 'New Home Inspections'),
      tradeSub('SC-362', 'Construction Stage Inspections'),
      tradeSub('SC-363', 'Pre-Handover Inspections'),
      tradeSub('SC-364', 'Defect Inspections'),
      tradeSub('SC-365', 'Dilapidation Inspections & Reports'),
      tradeSub('SC-366', 'Owner-Builder Inspections'),
      tradeSub('SC-367', 'Building Compliance Inspections'),
      tradeSub('SC-368', 'Pool Safety Inspections'),
      tradeSub('SC-369', 'Commercial Building Inspections'),
      tradeSub('SC-370', 'Asbestos Inspections'),
      tradeSub('SC-371', 'Asbestos Testing & Sampling'),
      tradeSub('SC-372', 'Asbestos Clearance Inspections & Air Monitoring'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 70, livePosts: 112, draftPosts: 8 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-032',
    name: 'Insulation',
    iconUrl: '',
    iconFileName: 'insulation-services.svg',
    tradeSubcategories: [
      tradeSub('SC-373', 'Ceiling Insulation'),
      tradeSub('SC-374', 'Wall Insulation'),
      tradeSub('SC-375', 'Underfloor Insulation'),
      tradeSub('SC-376', 'Acoustic Insulation'),
      tradeSub('SC-377', 'Thermal Insulation'),
      tradeSub('SC-378', 'Blow-In Insulation'),
      tradeSub('SC-379', 'Insulation Removal'),
      tradeSub('SC-380', 'Insulation Replacement'),
      tradeSub('SC-381', 'Retrofit Insulation'),
      tradeSub('SC-382', 'Commercial Insulation'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 87, livePosts: 139, draftPosts: 10 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-033',
    name: 'Handymen',
    iconUrl: '',
    iconFileName: 'handyman-services.svg',
    tradeSubcategories: [
      tradeSub('SC-383', 'General Handyman Services'),
      tradeSub('SC-384', 'Property Maintenance'),
      tradeSub('SC-385', 'General Home Repairs'),
      tradeSub('SC-386', 'Furniture & Flat-Pack Assembly'),
      tradeSub('SC-387', 'Picture, Mirror & TV Hanging'),
      tradeSub('SC-388', 'Shelving & Wall Mounting'),
      tradeSub('SC-389', 'Minor Carpentry'),
      tradeSub('SC-390', 'Door Repairs & Adjustments'),
      tradeSub('SC-391', 'Wall Patching'),
      tradeSub('SC-392', 'Minor Painting & Touch-Ups'),
      tradeSub('SC-393', 'Outdoor Repairs & Maintenance'),
      tradeSub('SC-394', 'Commercial Property Maintenance'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 104, livePosts: 166, draftPosts: 12 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-034',
    name: 'Locksmiths',
    iconUrl: '',
    iconFileName: 'locksmith.svg',
    tradeSubcategories: [
      tradeSub('SC-395', 'Residential Locksmithing'),
      tradeSub('SC-396', 'Commercial Locksmithing'),
      tradeSub('SC-397', 'Automotive Locksmithing'),
      tradeSub('SC-398', 'Emergency Lockouts'),
      tradeSub('SC-399', 'Lock Installation & Replacement'),
      tradeSub('SC-400', 'Lock Repairs'),
      tradeSub('SC-401', 'Lock Rekeying'),
      tradeSub('SC-402', 'Key Cutting & Duplication'),
      tradeSub('SC-403', 'Car Keys & Remotes'),
      tradeSub('SC-404', 'Smart & Digital Locks'),
      tradeSub('SC-405', 'Safes'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 121, livePosts: 194, draftPosts: 15 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-035',
    name: 'Shopfitting',
    iconUrl: '',
    iconFileName: 'shopfitting.svg',
    tradeSubcategories: [
      tradeSub('SC-406', 'Retail Fit-Outs'),
      tradeSub('SC-407', 'Office Fit-Outs'),
      tradeSub('SC-408', 'Hospitality Fit-Outs'),
      tradeSub('SC-409', 'Medical & Dental Fit-Outs'),
      tradeSub('SC-410', 'Commercial Fit-Outs'),
      tradeSub('SC-411', 'Shop Refurbishments'),
      tradeSub('SC-412', 'Commercial Joinery'),
      tradeSub('SC-413', 'Display Cabinets & Retail Displays'),
      tradeSub('SC-414', 'Partition Walls & Ceilings'),
      tradeSub('SC-415', 'Defit & Make-Good Services'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 138, livePosts: 221, draftPosts: 17 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-036',
    name: 'Stone Benchtops',
    iconUrl: '',
    iconFileName: 'stone-benchtops.svg',
    tradeSubcategories: [
      tradeSub('SC-416', 'Kitchen Stone Benchtops'),
      tradeSub('SC-417', 'Bathroom Stone Benchtops'),
      tradeSub('SC-418', 'Natural Stone Benchtops'),
      tradeSub('SC-419', 'Porcelain Benchtops'),
      tradeSub('SC-420', 'Stone Splashbacks'),
      tradeSub('SC-421', 'Stone Benchtop Installation'),
      tradeSub('SC-422', 'Stone Benchtop Replacement'),
      tradeSub('SC-423', 'Stone Benchtop Repairs'),
      tradeSub('SC-424', 'Stone Cutting & Polishing'),
      tradeSub('SC-425', 'Stone Benchtop Restoration'),
      tradeSub('SC-426', 'Commercial Stone Benchtops'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 155, livePosts: 248, draftPosts: 19 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-037',
    name: 'Screens & Doors',
    iconUrl: '',
    iconFileName: 'shower-screens.svg',
    tradeSubcategories: [
      tradeSub('SC-427', 'Fly Screens'),
      tradeSub('SC-428', 'Security Screens'),
      tradeSub('SC-429', 'Screen Doors'),
      tradeSub('SC-430', 'Security Doors'),
      tradeSub('SC-431', 'Sliding Screen Doors'),
      tradeSub('SC-432', 'Retractable Screens'),
      tradeSub('SC-433', 'Pet Doors'),
      tradeSub('SC-434', 'Screen Repairs'),
      tradeSub('SC-435', 'Security Door Repairs'),
      tradeSub('SC-436', 'Custom-Made Screens'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 172, livePosts: 275, draftPosts: 21 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-038',
    name: 'Staircases',
    iconUrl: '',
    iconFileName: 'stair-case.svg',
    tradeSubcategories: [
      tradeSub('SC-437', 'Timber Staircases'),
      tradeSub('SC-438', 'Steel Staircases'),
      tradeSub('SC-439', 'Concrete Staircases'),
      tradeSub('SC-440', 'Floating Staircases'),
      tradeSub('SC-441', 'Spiral Staircases'),
      tradeSub('SC-442', 'Custom Staircases'),
      tradeSub('SC-443', 'Staircase Installation & Replacement'),
      tradeSub('SC-444', 'Staircase Repairs'),
      tradeSub('SC-445', 'Staircase Renovations'),
      tradeSub('SC-446', 'Balustrades & Handrails'),
      tradeSub('SC-447', 'Commercial Staircases'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 189, livePosts: 302, draftPosts: 23 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-039',
    name: 'Security Systems',
    iconUrl: '',
    iconFileName: '',
    tradeSubcategories: [
      tradeSub('SC-448', 'Alarm Systems'),
      tradeSub('SC-449', 'CCTV & Security Cameras'),
      tradeSub('SC-450', 'Intercom Systems'),
      tradeSub('SC-451', 'Access Control Systems'),
      tradeSub('SC-452', 'Security Monitoring'),
      tradeSub('SC-453', 'Video Doorbells'),
      tradeSub('SC-454', 'Smart Security Systems'),
      tradeSub('SC-455', 'Security System Repairs & Maintenance'),
      tradeSub('SC-456', 'Commercial Security Systems'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 46, livePosts: 74, draftPosts: 6 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-040',
    name: 'Rendering',
    iconUrl: '',
    iconFileName: 'rendering.svg',
    tradeSubcategories: [
      tradeSub('SC-457', 'Cement Rendering'),
      tradeSub('SC-458', 'Acrylic Rendering'),
      tradeSub('SC-459', 'Stucco Rendering'),
      tradeSub('SC-460', 'Micro cement'),
      tradeSub('SC-461', 'Texture Coatings'),
      tradeSub('SC-462', 'Bagging'),
      tradeSub('SC-463', 'Exterior Rendering'),
      tradeSub('SC-464', 'Interior Rendering'),
      tradeSub('SC-465', 'Render Repairs & Patching'),
      tradeSub('SC-466', 'Render Restoration'),
      tradeSub('SC-467', 'Commercial Rendering'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 63, livePosts: 101, draftPosts: 8 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
  {
    id: 'TC-041',
    name: 'Caulking',
    iconUrl: '',
    iconFileName: 'chalking.svg',
    tradeSubcategories: [
      tradeSub('SC-468', 'General Caulking & Sealants'),
      tradeSub('SC-469', 'Bathroom & Shower Caulking'),
      tradeSub('SC-470', 'Kitchen Caulking'),
      tradeSub('SC-471', 'Window & Door Caulking'),
      tradeSub('SC-472', 'External & Façade Caulking'),
      tradeSub('SC-473', 'Expansion Joint Sealing'),
      tradeSub('SC-474', 'Precast Panel Caulking'),
      tradeSub('SC-475', 'Concrete Joint Sealing'),
      tradeSub('SC-476', 'Fire-Rated Sealants'),
      tradeSub('SC-477', 'Recaulking & Sealant Replacement'),
      tradeSub('SC-478', 'Commercial Caulking'),
    ],
    publicCategories: [],
    linkedRecords: { businesses: 80, livePosts: 128, draftPosts: 10 },
    createdOn: '12 May 2026',
    createdBy: 'Saim Sahi',
    lastUpdatedOn: '3 Jul 2026, 10:15 am',
    lastUpdatedBy: 'Saim Sahi',
  },
];

export const buildSampleTradeCategories = () =>
  assignAlphabeticalTradeCategoryIds(
    TRADE_CATEGORIES_DUMMY_DATA.map((c) => {
    const categoryIconUrl = c.iconUrl || resolveTradeCategoryIconSrc('', c.iconFileName);
    const publicCategoriesWithIcons = (c.publicCategories || []).map((pc) => {
      const iconFileName = pc.iconFileName || '';
      return {
        ...pc,
        iconFileName,
        iconUrl: pc.iconUrl || resolveTradeCategoryIconSrc('', iconFileName) || '',
        subcategories: (pc.subcategories || []).map((s) => ({ ...s })),
      };
    });

    const normalized = normalizeCategoryRecord({
      ...c,
      iconUrl: categoryIconUrl || '',
      tradeSubcategories: c.tradeSubcategories.map((s) => ({ ...s })),
      publicCategories: publicCategoriesWithIcons,
      linkedRecords: { ...c.linkedRecords },
    });

    const publicCategories = getPublicCategories(normalized);
    const publicFacingNames = getPublicFacingNames(normalized);

    return {
      ...normalized,
      iconUrl: normalized.iconUrl || categoryIconUrl || '',
      iconFileName: normalized.iconFileName || c.iconFileName || '',
      tradeSubcategories: (normalized.tradeSubcategories || []).map((s) => ({
        ...s,
        publicFacingIconUrl:
          s.publicFacingIconUrl ||
          resolveTradeCategoryIconSrc('', s.publicFacingIconFileName) ||
          '',
      })),
      directSubcategories: normalized.tradeSubcategories,
      publicCategories,
      publicCategory: publicCategories[0] || null,
      subcategories: normalized.tradeSubcategories.map((s) => ({ ...s, status: 'Active' })),
      status: normalized.id ? 'Active' : 'Inactive',
      publicFacingName: publicFacingNames[0] || normalized.name,
    };
  }),
  );
